import { Badge, BlockStack, Card, InlineStack, Text } from '@shopify/polaris'
import type { BccData } from './useBccData'
import { computeCashSafety } from './cashSafety'

type Severity = 'critical' | 'warning' | 'info' | 'success'

type Decision = {
  id:       string
  severity: Severity
  title:    string
  detail:   string
}

const BADGE_TONE: Record<Severity, 'critical' | 'warning' | 'info' | 'success'> = {
  critical: 'critical',
  warning:  'warning',
  info:     'info',
  success:  'success',
}

const BADGE_LABEL: Record<Severity, string> = {
  critical: 'Critical',
  warning:  'Warning',
  info:     'Info',
  success:  'Good',
}

function egp(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M EGP`
  if (n >= 1_000)     return `${Math.round(n / 1_000)}K EGP`
  return `${Math.round(n).toLocaleString('en-US')} EGP`
}

function buildDecisions(data: BccData): Decision[] {
  const decisions: Decision[] = []
  const { fulfillment: fi, dashboard, cash, exec } = data
  const fulfillment = fi.pipeline
  const summary = exec.summary as Record<string, number | null>

  // ── Blocked orders ─────────────────────────────────────────────────────────
  const blockedCount  = fulfillment.blockedHasPO.orderCount + fulfillment.blockedNoSolution.orderCount
  const blockedAmount = fulfillment.blockedHasPO.totalCodAmount + fulfillment.blockedNoSolution.totalCodAmount
  if (blockedCount > 0) {
    decisions.push({
      id:       'blocked-orders',
      severity: 'critical',
      title:    `${blockedCount} orders blocked — ${egp(blockedAmount)} at risk`,
      detail:   fulfillment.blockedNoSolution.orderCount > 0
        ? `${fulfillment.blockedNoSolution.orderCount} orders have no solution yet`
        : 'All blocked orders have a PO in progress',
    })
  }

  // ── Confirmation rate ───────────────────────────────────────────────────────
  const confirmRate = summary['confirmationRate'] as number | null
  if (confirmRate != null && confirmRate < 70) {
    decisions.push({
      id:       'low-confirmation',
      severity: 'critical',
      title:    `Confirmation rate at ${confirmRate.toFixed(1)}% — well below 80% target`,
      detail:   'Review call center performance and lead quality',
    })
  } else if (confirmRate != null && confirmRate < 78) {
    decisions.push({
      id:       'low-confirmation',
      severity: 'warning',
      title:    `Confirmation rate at ${confirmRate.toFixed(1)}% — below 80% target`,
      detail:   `Need ${(80 - confirmRate).toFixed(1)}pp improvement to reach target`,
    })
  }

  // ── Return rate ─────────────────────────────────────────────────────────────
  const returnRate = summary['returnRate'] as number | null
  if (returnRate != null && returnRate > 28) {
    decisions.push({
      id:       'high-returns',
      severity: 'critical',
      title:    `Return rate at ${returnRate.toFixed(1)}% — unusually high`,
      detail:   'Investigate product quality or delivery issues by region',
    })
  } else if (returnRate != null && returnRate > 22) {
    decisions.push({
      id:       'high-returns',
      severity: 'warning',
      title:    `Return rate at ${returnRate.toFixed(1)}% — above 22% threshold`,
      detail:   'Monitor closely — trending unfavorably',
    })
  }

  // ── Cash safety ─────────────────────────────────────────────────────────────
  // Same definition Financial Position renders — one question, one answer.
  const safety = computeCashSafety(data)

  if (safety.status === 'at_risk') {
    decisions.push({
      id:       'cash-shortfall',
      severity: 'critical',
      title:    `Cash shortfall — ${egp(-safety.surplusWithExpected)} uncovered`,
      detail:   `${egp(safety.arrived)} collected plus ${egp(safety.expected)} awaiting remittance, against ${egp(safety.totalOut)} in costs`,
    })
  } else if (safety.status === 'tight') {
    decisions.push({
      id:       'cash-tight',
      severity: 'warning',
      title:    `Cash is tight — ${egp(-safety.surplus)} short until settlements land`,
      detail:   `${egp(safety.expected)} awaiting remittance covers the gap`,
    })
  }

  if (!safety.hasFixedCosts) {
    decisions.push({
      id:       'no-fixed-expenses',
      severity: 'warning',
      title:    'No fixed expenses recorded — profit and cash checks are overstated',
      detail:   'Add rent, payroll and subscriptions under Financial Engine → Fixed Expenses',
    })
  }

  // ── Oldest unsettled ────────────────────────────────────────────────────────
  if (cash.oldestUnsettledDays != null && cash.oldestUnsettledDays > 14) {
    decisions.push({
      id:       'old-unsettled',
      severity: 'warning',
      title:    `Oldest unsettled order is ${cash.oldestUnsettledDays} days old`,
      detail:   `${cash.unsettledOrders} orders worth ${egp(cash.totalCodAtRisk)} not yet settled`,
    })
  }

  // ── Delivery rate ───────────────────────────────────────────────────────────
  // Sourced from /executive/dashboard, not /finance/dashboard: the executive
  // figure is delivered ÷ dispatched, so orders that have not shipped yet do not
  // drag it down. The finance figure divides by every order in the month, which
  // reads as a crash early in the month and would fire a false critical alert.
  const deliveryRate = summary['deliveryRate'] as number | null
  if (deliveryRate != null && deliveryRate < 72) {
    decisions.push({
      id:       'low-delivery',
      severity: 'critical',
      title:    `Delivery rate at ${deliveryRate.toFixed(1)}% — critical`,
      detail:   'Investigate carrier performance or address accuracy issues',
    })
  } else if (deliveryRate != null && deliveryRate < 79) {
    decisions.push({
      id:       'low-delivery',
      severity: 'warning',
      title:    `Delivery rate at ${deliveryRate.toFixed(1)}% — below 79% target`,
      detail:   `${dashboard.delivery.deliveredCount} delivered this month`,
    })
  }

  // ── Data confidence ─────────────────────────────────────────────────────────
  if (cash.confidence.level === 'low') {
    decisions.push({
      id:       'low-confidence',
      severity: 'info',
      title:    'Data confidence is low — numbers may be incomplete',
      detail:   cash.confidence.missingData.join(', ') || cash.confidence.reasons.join(', '),
    })
  }

  // ── All good fallback ───────────────────────────────────────────────────────
  if (decisions.length === 0) {
    decisions.push({
      id:       'all-good',
      severity: 'success',
      title:    'No critical issues detected',
      detail:   'All key metrics are within acceptable ranges',
    })
  }

  // Sort: critical first, then warning, then info, then success
  const ORDER: Severity[] = ['critical', 'warning', 'info', 'success']
  return decisions.sort((a, b) => ORDER.indexOf(a.severity) - ORDER.indexOf(b.severity))
}

type Props = { data: BccData }

export function TodayDecisions({ data }: Props) {
  const decisions = buildDecisions(data)

  return (
    <Card>
      <BlockStack gap="400">
        <InlineStack align="space-between" blockAlign="center">
          {/* Rules span both lenses (cash shortfall + performance rates), so the
              card carries no single lens badge — each decision names its own basis. */}
          <Text as="h2" variant="headingMd">Today's Decisions</Text>
          <Text as="p" variant="bodySm" tone="subdued">
            {decisions.filter(d => d.severity === 'critical').length} critical
          </Text>
        </InlineStack>

        <BlockStack gap="200">
          {decisions.map(d => (
            <div
              key={d.id}
              style={{
                display: 'flex',
                gap: 12,
                alignItems: 'flex-start',
                background: 'var(--p-color-bg-surface-secondary)',
                border: '1px solid var(--p-color-border)',
                borderRadius: 8,
                padding: '10px 14px',
              }}
            >
              <div style={{ paddingTop: 2 }}>
                <Badge tone={BADGE_TONE[d.severity]}>{BADGE_LABEL[d.severity]}</Badge>
              </div>
              <BlockStack gap="100">
                <Text as="p" variant="bodyMd" fontWeight="semibold">{d.title}</Text>
                <Text as="p" variant="bodySm" tone="subdued">{d.detail}</Text>
              </BlockStack>
            </div>
          ))}
        </BlockStack>
      </BlockStack>
    </Card>
  )
}
